import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Connection as ConnectionSchema, ConnectionTestResult } from '@cloudsign/shared';
import type { Connection, ConnectionKind } from '@cloudsign/shared';
import { Button, Card, ConfirmButton, TextField } from '../../components/ui';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { StatusPill } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { connectionsKey } from './api';

const TONE = { active: 'good', failing: 'warn', disabled: 'neutral' } as const;

/** Slack or Teams: the connections of one kind. The web address is write-only; only a masked form is ever shown. */
export function ConnectionsCard({ kind, connections, canManage }: { kind: ConnectionKind; connections: Connection[]; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Connection | 'new' | null>(null);
  const [tests, setTests] = useState<Record<string, ConnectionTestResult>>({});
  const refresh = () => qc.invalidateQueries({ queryKey: connectionsKey });
  const test = useMutation({
    mutationFn: (id: string) => api.post(`/integrations/connections/${id}/test`, { schema: ConnectionTestResult }).then((r) => ({ id, r })),
    onSuccess: ({ id, r }) => {
      setTests((x) => ({ ...x, [id]: r }));
      void refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (c: Connection) => api.patch(`/integrations/connections/${c.id}`, { body: { status: c.status === 'disabled' ? 'active' : 'disabled' }, schema: ConnectionSchema }),
    onSuccess: () => void refresh(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/integrations/connections/${id}`),
    onSuccess: () => {
      toast(t('p4w27.conn.deleted'));
      void refresh();
    },
  });
  const add = canManage ? (
    <Button variant="primary" onClick={() => setEditing('new')} disabled={editing !== null}>
      {t('p4w27.conn.add')}
    </Button>
  ) : undefined;
  return (
    <Card title={t(`p4w27.conn.${kind}.title`)} description={t(`p4w27.conn.${kind}.description`)} footer={add}>
      {connections.length === 0 && editing === null ? <p className="text-sm text-slate-600">{t('p4w27.conn.empty')}</p> : null}
      <ul className="divide-y divide-slate-100" aria-label={t('p4w27.conn.listLabel', { kind: t(`p4w27.conn.${kind}.title`) })}>
        {connections.map((c) => {
          const result = tests[c.id];
          return (
            <li key={c.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {c.name}
                  <StatusPill tone={TONE[c.status]}>{t(`p4w27.conn.status.${c.status}`)}</StatusPill>
                </p>
                <p className="break-all font-mono text-xs text-slate-600">{c.targetMasked}</p>
                <p className="text-xs text-slate-600">{c.lastUsedAt ? t('p4w27.conn.lastUsed', { when: formatDateTime(c.lastUsedAt) }) : t('p4w27.conn.neverUsed')}</p>
                {c.consecutiveFailures > 0 ? (
                  <p className="text-xs text-amber-800">
                    {t('p4w27.conn.failures', { count: c.consecutiveFailures })} {c.lastErrorKey ? t(c.lastErrorKey) : ''}
                  </p>
                ) : null}
                <div aria-live="polite">
                  {result ? (
                    <p className={`text-sm ${result.ok ? 'text-green-800' : 'text-red-700'}`}>
                      {result.ok ? t('p4w27.conn.testOk') : `${t('p4w27.conn.testFailed')} ${result.errorKey ? t(result.errorKey) : ''}`} {t('p4w27.conn.testTime', { ms: result.durationMs })}
                    </p>
                  ) : null}
                </div>
              </div>
              {canManage ? (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button aria-label={t('p4w27.conn.testLabel', { name: c.name })} disabled={test.isPending} onClick={() => test.mutate(c.id)}>
                    {t('p4w27.conn.test')}
                  </Button>
                  <Button aria-label={t('p4w27.conn.editLabel', { name: c.name })} onClick={() => setEditing(c)}>
                    {t('p4w27.conn.edit')}
                  </Button>
                  <Button
                    aria-label={t('p4w27.conn.toggleLabel', { action: c.status === 'disabled' ? t('p4w27.conn.enable') : t('p4w27.conn.disable'), name: c.name })}
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate(c)}
                  >
                    {c.status === 'disabled' ? t('p4w27.conn.enable') : t('p4w27.conn.disable')}
                  </Button>
                  <ConfirmButton label={t('p4w27.conn.delete')} confirmLabel={t('p4w27.conn.deleteConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate(c.id)} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <ErrorLine error={test.error ?? toggle.error ?? remove.error} />
      {editing !== null ? (
        <ConnectionForm
          key={editing === 'new' ? 'new' : editing.id}
          kind={kind}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast(t('common.saved'));
            void refresh();
          }}
        />
      ) : null}
    </Card>
  );
}

function ConnectionForm({ kind, existing, onClose, onSaved }: { kind: ConnectionKind; existing: Connection | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(existing?.name ?? '');
  const [url, setUrl] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), ...(url.trim() ? { url: url.trim() } : {}) };
      return existing
        ? api.patch(`/integrations/connections/${existing.id}`, { body, schema: ConnectionSchema })
        : api.post('/integrations/connections', { body: { ...body, kind, url: url.trim() }, schema: ConnectionSchema });
    },
    onSuccess: onSaved,
  });
  const errs = fieldErrors(save.error);
  const valid = name.trim() !== '' && (existing !== null || url.trim() !== '');
  return (
    <form
      className="mt-4 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      {existing === null ? (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">{t('p4w27.conn.stepsTitle')}</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-slate-700">
            {[1, 2, 3].map((n) => (
              <li key={n}>{t(`p4w27.conn.${kind}.step${n}`)}</li>
            ))}
          </ol>
        </details>
      ) : null}
      <TextField label={t('p4w27.conn.name')} hint={t('p4w27.conn.nameHint')} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} error={errs['name']} />
      <TextField
        label={existing ? t('p4w27.conn.urlNew') : t('p4w27.conn.url')}
        hint={t('p4w27.conn.urlHint')}
        type="password"
        autoComplete="off"
        spellCheck={false}
        value={url}
        maxLength={2000}
        onChange={(e) => setUrl(e.target.value)}
        error={errs['url']}
      />
      {Object.keys(errs).length === 0 ? <ErrorLine error={save.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={!valid || save.isPending}>
          {t('p4w27.conn.save')}
        </Button>
        <Button onClick={onClose}>{t('p4w27.conn.cancel')}</Button>
      </div>
    </form>
  );
}
